<script>
import { eventGroupState } from "@app/store/eventGroupStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import {
    playerState,
    updatePreviewEvent,
} from "@app/store/playerStore.svelte.js";
import { lockState } from "@app/store/lockStore.svelte.js";
import { styleState } from "@app/store/styleStore.svelte.js";
import convertToPlainText from "@cclib/functions/quill/convertToPlainText.js";

const captionStyleMap = {
    "Pop-On": "",
    "Paint-On": "paint-on",
    "Roll-Up 2": "roll-up",
    "Roll-Up 3": "roll-up",
    "Roll-Up 4": "roll-up",
};

const aspectRatioMap = {
    "16/9": 1.78,
    "4/3": 1.33,
    "1/1": 1,
    "9/16": 0.56,
    "21/9": 2.33,
    "143/100": 1.43,
    "185/100": 1.85,
    "235/100": 2.35,
    "239/100": 2.39,
    "276/100": 2.76,
};

let previewWidth = $state(80);
let previewHeight = $state(20);
let subtitlePreviewWidth = $state(80);
let subtitlePreviewHeight = $state(20);

let yPadding = $derived.by(() => {
    let padding = styleState.yPadding;
    let mediaAspectRatio = (projectState?.media?.aspectRatio || "16/9").replace(":", "/");
    let mediaAspectRatioValue = aspectRatioMap[mediaAspectRatio] || aspectRatioMap["16/9"];
    let previewAspectRatio = (previewWidth / previewHeight).toFixed(2);

    if (previewAspectRatio != mediaAspectRatioValue) {
        let videoHeight = previewWidth / mediaAspectRatioValue;
        let heightDiff = previewHeight - videoHeight;
        return videoHeight * (padding / 100) + heightDiff / 2;
    } else {
        return previewHeight * (padding / 100);
    }
});

let xPadding = $derived.by(() => {
    let padding = styleState.xPadding;
    return previewWidth * (padding / 100);
});

/**
 * Check if an event style is a Roll-Up style.
 */
function isRollUp(style) {
    return style === "Roll-Up 2" || style === "Roll-Up 3" || style === "Roll-Up 4";
}

/**
 * Get the number of visible lines for a Roll-Up style (2, 3, or 4).
 */
function getRollUpLineCount(style) {
    switch (style) {
        case "Roll-Up 2": return 2;
        case "Roll-Up 3": return 3;
        case "Roll-Up 4": return 4;
        default: return 1;
    }
}

/**
 * For a Roll-Up event at the given index, collect the text from the
 * current event and preceding consecutive Roll-Up events (up to the
 * roll-up line count). Stops if a Pop-On or Paint-On event is encountered.
 * Returns the combined HTML text string.
 */
function getRollUpText(currentIndex) {
    const events = eventGroupState[projectState.selected]?.events;
    if (!events || currentIndex < 0) return "";
    
    const currentEvent = events[currentIndex];
    if (!currentEvent || !isRollUp(currentEvent.style)) return currentEvent?.text || "";
    
    const maxLines = getRollUpLineCount(currentEvent.style);
    let collected = [];
    
    for (let i = currentIndex; i >= 0 && collected.length < maxLines; i--) {
        const ev = events[i];
        if (!isRollUp(ev.style) || !convertToPlainText(ev.text)) break; // Stop at Pop-On or Paint-On or if the event has no text
        collected.unshift(ev.text); // Prepend to maintain chronological order
    }
    
    return collected.join("");
}

/**
 * Derived: combined text for the current preview event.
 * For Roll-Up events, concatenates text from preceding Roll-Up events.
 * For Pop-On/Paint-On, returns the single event's text.
 */
let previewText = $derived.by(() => {
    const events = eventGroupState[projectState.selected]?.events;
    const idx = playerState.previewEvent;
    if (!events || idx < 0 || !events[idx]) return "";
    
    if (isRollUp(events[idx].style) && convertToPlainText(events[idx].text).trim()) {
        return getRollUpText(idx);
    }
    return events[idx].text;
});

function getEvent() {
    if (playerState.playing) {
        updatePreviewEvent(getEventByTime(playerState.time));

        /* Scroll to and select the event if caption lock is enabled */
        if (
            lockState.caption &&
            playerState.previewEvent !== -1 &&
            eventGroupState[projectState.selected].selected[0] !==
                playerState.previewEvent
        ) {
            eventGroupState[projectState.selected].selected = [
                playerState.previewEvent,
            ];

            const selectedIndex =
                eventGroupState[projectState.selected].selected[0];

            // Dispatch custom event to scroll to the selected event
            window.dispatchEvent(
                new CustomEvent("scrollToVirtualListItem", {
                    detail: { index: selectedIndex },
                }),
            );
        }
    } else {
        updatePreviewEvent(eventGroupState[projectState.selected]?.selected[0]);
    }
}

/**
 * Find the active event index at the given time.
 * For Roll-Up events (which have no meaningful end time), the event is
 * active from its start time until the next event starts.
 * For Pop-On/Paint-On events, uses the standard start <= sec <= end check.
 */
function getEventByTime(sec) {
    const events = eventGroupState[projectState.selected]?.events;
    if (!events || events.length === 0) return -1;
    
    // Iterate backwards to find the latest event that has started
    for (let i = events.length - 1; i >= 0; i--) {
        const event = events[i];
        if (event.start !== false && event.start <= sec) {
            if (isRollUp(event.style)) {
                // Roll-Up events are active from start until the next event starts
                const nextEvent = events[i + 1];
                if (!nextEvent || nextEvent.start === false || nextEvent.start > sec) {
                    return i;
                }
            } else {
                // Pop-On/Paint-On: check start and end
                if (event.end !== false && event.end >= sec) {
                    return i;
                }
            }
        }
    }
    return -1;
}

function getFlexAlignment(pos, isX = true) {
    if (typeof pos === "number") {
        // For numeric positions, use start alignment for precise positioning
        // This allows the transform to work from a consistent baseline
        return "start";
    }

    // For string positions, map to flexbox values
    switch (pos) {
        case "start":
            return "start";
        case "center":
            return "center";
        case "end":
            return "end";
        default:
            return isX ? "center" : "end";
    }
}

function calculatePixelOffset(pos, isX = true) {
    if (typeof pos !== "number") return 0;
    const maxPos = isX ? styleState.width : styleState.height;

    if (isX) {
        // For horizontal positioning with monospace font
        // Calculate character width based on available space
        const charWidth = subtitlePreviewWidth / maxPos;
        // Position is 1-based, convert to 0-based
        const zeroBasedPos = pos - 1;
        // Calculate the starting pixel position for this character column
        // Since we're using flexbox 'start' alignment, offset from the left edge
        return zeroBasedPos * charWidth;
    } else {
        // For vertical positioning, use line-based positioning
        const lineHeight = subtitlePreviewHeight / maxPos;

        // Position is 1-based, convert to 0-based
        const zeroBasedPos = pos - 1;

        // Calculate the top of the line (since we use 'start' for Y alignment too)
        return zeroBasedPos * lineHeight;
    }
}

function decToHex(value) {
    return Math.floor((value / 100) * 255)
        .toString(16)
        .padStart(2, "0");
}

$effect(() => {
    getEvent(
        playerState.time,
        eventGroupState[projectState.selected]?.selected[0],
    );
});
</script>

<!-- horizontal alignmnet: justify-content-start, justify-content-center, justify-content-end -->
<!-- vertical alignmnet: align-items-start, align-items-center, align-items-end -->

<div
    id="SubtitlePreviewContainer"
    style="padding: {yPadding}px {xPadding}px;"
    bind:clientWidth={previewWidth}
    bind:clientHeight={previewHeight}>
    <div
        id="SubtitlePreviewWrapper"
        class="border w-100 h-100 d-flex justify-content-{getFlexAlignment(
            eventGroupState[projectState?.selected]?.events[
                playerState?.previewEvent
            ]?.xPos,
            true,
        )} align-items-{getFlexAlignment(
            eventGroupState[projectState?.selected]?.events[
                playerState?.previewEvent
            ]?.yPos,
            false,
        )}"
        bind:clientWidth={subtitlePreviewWidth}
        bind:clientHeight={subtitlePreviewHeight}>
        {#if lockState.preview && eventGroupState[projectState?.selected]?.events[playerState.previewEvent]}
            <div
                id="SubtitlePreview"
                class={captionStyleMap[
                    eventGroupState[projectState.selected].events[
                        playerState.previewEvent
                    ].style
                ]}
                style="
                direction: {eventGroupState[projectState.selected].rtl
                    ? 'rtl'
                    : 'ltr'}; 
                writing-mode: {eventGroupState[projectState.selected].events[
                    playerState.previewEvent
                ].writingMode === 'tbrl'
                    ? 'vertical-rl'
                    : 'horizontal-tb'};
                transform: translate({calculatePixelOffset(
                    eventGroupState[projectState.selected].events[
                        playerState.previewEvent
                    ].xPos,
                    true,
                )}px, {calculatePixelOffset(
                    eventGroupState[projectState.selected].events[
                        playerState.previewEvent
                    ].yPos,
                    false,
                )}px);
                font-family: {styleState.fontFamily || 'plexMono'}; 
                font-size: {styleState.fontSize || 16}px;
                color: {styleState.color + decToHex(styleState.opacity)};
                background: {styleState.outlineColor || 'black'};
                line-height: 110%;
                letter-spacing: {styleState.spacing}px;
                {styleState.borderStyle == '1'
                    ? `text-shadow: ${styleState.shadow}px ${styleState.shadow}px 3px ${styleState.shadowColor + decToHex(styleState.shadowOpacity)}; 
      -webkit-text-stroke: ${styleState.outline}px ${styleState.outlineColor + decToHex(styleState.outlineOpacity)};
      background: none;`
                    : `background: ${styleState.outlineColor + decToHex(styleState.outlineOpacity)}; 
      padding: ${styleState.outline}px`};
                text-align: {eventGroupState[projectState.selected].events[
                    playerState.previewEvent
                ].alignment};
                text-wrap: nowrap; 
                overflow: hidden;">
                {@html previewText}
            </div>
        {/if}
    </div>
</div>

<style>
#SubtitlePreviewContainer {
    z-index: 9999;
    position: absolute;
    pointer-events: none;
    overflow: hidden;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
}

#SubtitlePreviewWrapper {
    pointer-events: none;
    overflow: hidden;
}

#SubtitlePreview {
    padding: 1px;
    position: relative;
    user-select: none;
}

#SubtitlePreview :global(p) {
    margin: 0;
}
</style>
